/**
 * Copyright 2026 Ziz Academy
 */

import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { examApi, submissionApi } from '../services/api';
import { Exam, ExamSubmission, StudentAnswer } from '../types';

const EvaluateSubmissions: React.FC = () => {
    const { examId } = useParams<{ examId: string }>();
    const [exam, setExam] = useState<Exam | null>(null);
    const [submissions, setSubmissions] = useState<ExamSubmission[]>([]);
    const [selectedSubmission, setSelectedSubmission] = useState<ExamSubmission | null>(null);
    const [loading, setLoading] = useState(true);
    const [evaluating, setEvaluating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [examData, submissionsData] = await Promise.all([
                    examApi.getById(Number(examId)),
                    submissionApi.getByExam(Number(examId)),
                ]);
                setExam(examData);
                setSubmissions(submissionsData);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [examId]);

    const loadSubmissionDetails = async (submissionId: number) => {
        try {
            setError(null);
            const data = await submissionApi.getById(submissionId);
            setSelectedSubmission(data);
        } catch (error) {
            console.error('Error fetching submission:', error);
            setError('Failed to load submission details. Please try again.');
            setSelectedSubmission(null);
        }
    };

    const handleEvaluate = async (studentAnswerId: number, marksAwarded: number, feedback: string) => {
        setEvaluating(true);
        setError(null);
        try {
            const updatedSubmission = await submissionApi.evaluate({
                studentAnswerId,
                marksAwarded,
                feedback,
            });
            setSelectedSubmission(updatedSubmission);

            // Update submissions list
            setSubmissions(submissions.map((s) =>
                s.id === updatedSubmission.id
                    ? { ...s, totalScore: updatedSubmission.totalScore, isEvaluated: updatedSubmission.isEvaluated }
                    : s
            ));
            setSuccessMessage('✅ Evaluation saved successfully!');
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (error) {
            console.error('Error evaluating:', error);
            setError('Failed to save evaluation. Please try again.');
        } finally {
            setEvaluating(false);
        }
    };

    if (loading) {
        return (
            <div className="loading" data-testid="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    if (!exam) {
        return (
            <div className="empty-state">
                <div className="empty-state-emoji">😕</div>
                <h3 className="empty-state-title">Exam Not Found</h3>
            </div>
        );
    }

    return (
        <div data-testid="evaluate-submissions">
            <div className="card" style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white' }}>
                <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
                    📝 Evaluate: {exam.title}
                </h1>
                <p style={{ opacity: 0.9 }}>
                    Review and grade student submissions
                </p>
            </div>

            {error && (
                <div style={{
                    backgroundColor: '#fed7d7',
                    color: '#c53030',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    border: '1px solid #fc8181'
                }}>
                    ❌ {error}
                </div>
            )}

            {successMessage && (
                <div style={{
                    backgroundColor: '#c6f6d5',
                    color: '#22543d',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    border: '1px solid #9ae6b4'
                }}>
                    {successMessage}
                </div>
            )}

            {submissions.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-emoji">📭</div>
                    <h3 className="empty-state-title">No Submissions Yet</h3>
                    <p>Students haven't submitted this exam yet.</p>
                    <Link to="/" className="btn btn-primary" style={{ marginTop: '1rem' }}>
                        🏠 Back to Dashboard
                    </Link>
                </div>
            ) : (
                <div className="grid grid-2">
                    {/* Submissions List */}
                    <div>
                        <h3 style={{ marginBottom: '1rem' }}>👥 Submissions ({submissions.length})</h3>
                        {submissions.map((sub) => (
                            <div
                                key={sub.id}
                                className="card"
                                style={{
                                    cursor: 'pointer',
                                    backgroundColor: selectedSubmission?.id === sub.id ? '#667eea' : 'transparent',
                                    borderWidth: '3px',
                                    borderStyle: 'solid',
                                }}
                                onClick={() => loadSubmissionDetails(sub.id!)}
                                data-testid={`submission-${sub.id}`}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <strong>{sub.studentName}</strong>
                                        <div style={{ fontSize: '0.875rem', color: '#718096' }}>
                                            {sub.submittedAt ? new Date(sub.submittedAt).toLocaleString() : 'N/A'}
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
                                            {sub.totalScore}/{sub.totalMarks}
                                        </div>
                                        {sub.isEvaluated ? (
                                            <span className="badge badge-success">✅ Evaluated</span>
                                        ) : (
                                            <span className="badge badge-warning">⌛ Pending</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Submission Details */}
                    <div>
                        {selectedSubmission ? (
                            <>
                                <h3 style={{ marginBottom: '1rem' }}>
                                    📄 {selectedSubmission.studentName}'s Answers
                                </h3>
                                {selectedSubmission.studentAnswers && selectedSubmission.studentAnswers.length > 0 ? (
                                    <>
                                        {selectedSubmission.studentAnswers.map((answer, index) => (
                                            <EvaluateAnswerCard
                                                key={answer.id}
                                                answer={answer}
                                                index={index}
                                                onEvaluate={handleEvaluate}
                                                isEvaluating={evaluating}
                                            />
                                        ))}
                                        {selectedSubmission.studentAnswers.every(a => !a.textAnswer && a.selectedAnswerId) && (
                                            <div style={{
                                                backgroundColor: '#edf2f7',
                                                padding: '1.5rem',
                                                borderRadius: '10px',
                                                textAlign: 'center',
                                                marginTop: '1rem'
                                            }}>
                                                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🎯</div>
                                                <p style={{ color: '#2d3748', fontWeight: '500' }}>All answers are MCQ type and have been auto-graded.</p>
                                                <p style={{ color: '#718096', fontSize: '0.875rem' }}>No additional evaluation needed.</p>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="empty-state">
                                        <div className="empty-state-emoji">📭</div>
                                        <h3 className="empty-state-title">No Answers Found</h3>
                                        <p>This submission has no answers.</p>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="empty-state">
                                <div className="empty-state-emoji">👈</div>
                                <h3 className="empty-state-title">Select a Submission</h3>
                                <p>Click on a submission to view and evaluate it</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

interface EvaluateAnswerCardProps {
    answer: StudentAnswer;
    index: number;
    onEvaluate: (studentAnswerId: number, marks: number, feedback: string) => void;
    isEvaluating: boolean;
}

const EvaluateAnswerCard: React.FC<EvaluateAnswerCardProps> = ({
    answer,
    index,
    onEvaluate,
    isEvaluating,
}) => {
    const maxMarks = typeof answer.maxMarks === 'number' ? answer.maxMarks : 0;
    const [marks, setMarks] = useState(() => {
        const initial = typeof answer.marksAwarded === 'number' ? answer.marksAwarded : 0;
        return Math.max(0, Math.min(initial, maxMarks));
    });
    const [feedback, setFeedback] = useState(answer.feedback || '');
    // Subjective question = has textAnswer, MCQ = has selectedAnswerId
    const isSubjective = !!answer.textAnswer;

    const handleSubmit = () => {
        const clamped = Math.max(0, Math.min(marks, maxMarks));
        if (clamped !== marks) {
            alert(`Marks must be between 0 and ${maxMarks}. It has been adjusted to ${clamped}.`);
        }
        onEvaluate(answer.id!, clamped, feedback);
    }

    return (
        <div
            className="question-card"
            style={{
                borderColor: answer.isCorrect === true || (answer.marksAwarded && answer.marksAwarded > 0)
                    ? '#38ef7d'
                    : answer.isCorrect === false
                        ? '#f45c43'
                        : '#667eea'
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <div>
                    <span className="question-number">{index + 1}:</span>
                    <span className="badge badge-info" style={{ marginLeft: '0.5rem' }}>
                        {isSubjective ? '✍ Subjective' : '🔘 MCQ'}
                    </span>
                </div>
                <span className="question-marks">
                    {answer.marksAwarded ?? '?'}/{answer.maxMarks}
                </span>
            </div>

            {/* Render rich question text (HTML) if available; fallback to plain text and optional image */}
            {answer.questionRichText ? (
                <div
                    className="question-text"
                    style={{ lineHeight: 1.5 }}
                    dangerouslySetInnerHTML={{ __html: answer.questionRichText }}
                />
            ) : (
                <>
                    <p className="question-text">{answer.questionText}</p>
                    {answer.questionImagePath && (
                        <div style={{ marginTop: '0.75rem' }}>
                            <img
                                src={answer.questionImagePath}
                                alt="Question"
                                style={{ maxWidth: '100%', maxHeight: '380px', borderRadius: '10px', border: '1px solid #e2e8f0' }}
                            />
                        </div>
                    )}
                </>
            )}

            {answer.selectedAnswerText && (
                <div 
                className={`answer-option ${answer.isCorrect ? 'correct' : 'incorrect'}`}
                    style={{ marginTop: '1rem', cursor: 'default' }}
                    >
                    <span>{answer.selectedAnswerText}</span>
                    {answer.isCorrect ? <span style={{ marginLeft: 'auto' }}>✅</span> : <span style={{ marginLeft: 'auto' }}>❌</span>}
                </div>
            )}

            {answer.textAnswer && (
                <div style={{ marginTop: '1rem' }}>
                    <strong>Student's Answer:</strong>
                    <p style={{ background: '#f0f4f8', padding: '1rem', borderRadius: '10px', marginTop: '0.5rem' }}>
                        {answer.textAnswer}
                        </p>
                </div>
            )}

            {isSubjective && (
                <div style={{ marginTop: '1rem', padding: '1rem', background: '#f0f4f8', borderRadius: '15px' }}>
                    <div className="form-group">
                        <label className="form-label">⭐ Marks (max: {answer.maxMarks})  </label>
                    <input
                        type="number"
                        className="form-input"
                        min="0"
                        max={answer.maxMarks}
                        value={marks}
                        onChange={(e) => {
                            const num = Number(e.target.value);
                            const safe = Number.isFinite(num) ? Math.max(0, Math.min(num, maxMarks)) : 0;
                            setMarks(safe);
                        }}
                        disabled={answer.isEvaluated}
                        data-testid={`marks-input-${answer.id}`}
                    />
                    </div>
                    <div className="form-group">
                    <label className="form-label">💬 Feedback</label>
                    <textarea
                        className="form-input"
                        placeholder="Write feedback for the student..."
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        disabled={answer.isEvaluated}
                        data-testid={`feedback-input-${answer.id}`}
                    />
                </div>
                {!answer.isEvaluated && (
                    <button
                        className="btn btn-success"
                        onClick={handleSubmit}
                        disabled={isEvaluating}
                        data-testid={`evaluate-btn-${answer.id}`}
                    >
                        {isEvaluating ? '⌛ Saving...' : '✅ Save Evaluation'}
                    </button>
                )}
                {answer.isEvaluated && (
                    <div className="badge badge-success">✅ Already Evaluated</div>
                )}
        </div>
    )}
    </div>
    );
};

export default EvaluateSubmissions;